import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Product } from '@portal/portal-core';
import { validateProductVersionCreation } from 'utils/product-version-validation';
import { validateSemanticVersion } from './validation-utils';

interface ProductOption {
  namesValue: string;
  displayName: string;
}

interface FormErrors {
  product?: string;
  version?: string;
}

interface ProductVersionPayload {
  productNameConstant: string;
  semanticProductVersion: string;
  whitelistedCountries: string[];
  active: boolean;
}

interface CreateProductVersionModalProps {
  onSave: (payload: ProductVersionPayload) => void;
  onCancel: () => void;
}

// Product.NAMES_LICENSED_PRODUCTS / NAMES / PRODUCT_MARKETING_NAMES are typed as `any`
// by cloud-core, so indexing below resolves to `any`.
const PRODUCT_OPTIONS: ProductOption[] = Object.entries(Product.NAMES_LICENSED_PRODUCTS).map(
  ([key, value]) => ({
    namesValue: Product.NAMES[key],
    displayName: Product.PRODUCT_MARKETING_NAMES[value as string],
  }),
);

const CreateProductVersionModal = ({
  onSave,
  onCancel,
}: CreateProductVersionModalProps): React.JSX.Element => {
  const [productNameConstant, setProductNameConstant] = useState('');
  const [semanticProductVersion, setSemanticProductVersion] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!productNameConstant) {
      newErrors.product = 'Product is required';
    }
    if (!semanticProductVersion) {
      newErrors.version = 'Version is required';
    } else {
      // Use centralized validation
      const versionValidation = validateSemanticVersion(semanticProductVersion);
      if (!versionValidation.valid) {
        newErrors.version = versionValidation.error;
      } else if (productNameConstant) {
        // Validate product version creation rules
        const creationValidation = validateProductVersionCreation(
          productNameConstant,
          semanticProductVersion,
        );
        if (!creationValidation.allowed) {
          newErrors.version = creationValidation.error;
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        productNameConstant,
        semanticProductVersion,
        whitelistedCountries: [], // Start with empty whitelist (all countries allowed)
        active: false, // Start product versions in disabled state
      });
    }
  };

  return (
    <ModalContainer onClick={handleBackdropClick}>
      <ModalContent>
        <Header>
          <Title>Create New Product Version</Title>
          <Subtitle>Add a new product version to configure region controls</Subtitle>
        </Header>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="product">Product *</Label>
            <Select
              id="product"
              value={Product.NAMES[productNameConstant]}
              onChange={(e) => setProductNameConstant(e.target.value)}
              hasError={!!errors.product}
            >
              <option value="">Select a product...</option>
              {PRODUCT_OPTIONS.map((product) => (
                <option key={product.namesValue} value={product.namesValue}>
                  {product.displayName} ({product.namesValue})
                </option>
              ))}
            </Select>
            {errors.product && <ErrorText>{errors.product}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="version">Semantic Version *</Label>
            <Input
              id="version"
              type="text"
              placeholder="e.g., 1.0.0"
              value={semanticProductVersion}
              onChange={(e) => setSemanticProductVersion(e.target.value)}
              hasError={!!errors.version}
            />
            {errors.version && <ErrorText>{errors.version}</ErrorText>}
            <HelpText>Format: MAJOR.MINOR.PATCH (e.g., 3.1.0)</HelpText>
          </FormGroup>

          <InfoBox>
            <InfoIcon>ℹ️</InfoIcon>
            <InfoText>
              The new product version will be created in a <strong>disabled</strong> state. You will
              need to enable it after creation for it to be available to users. By default, it will
              be available in all countries once enabled.
            </InfoText>
          </InfoBox>

          <ButtonContainer>
            <CancelButton type="button" onClick={onCancel}>
              Cancel
            </CancelButton>
            <SaveButton type="submit">Create Product Version</SaveButton>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </ModalContainer>
  );
};

export default CreateProductVersionModal;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above the table-toolbar dropdown (1002) and the FjTooltip visor (.fj-tooltip-portal .visor,
     1001) - both share this component's stacking context via document.body portals, and a modal
     must render above either if one is left open/lingering underneath it. */
  z-index: 1003;
`;

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const Header = styled.div`
  padding: 24px 24px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.notSelectable};
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.productTitle};
`;

const Form = styled.form`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.error || '#d32f2f' : theme.colors.selectableLightBg};
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: 2px solid
      ${({ theme, hasError }) =>
        hasError ? theme.colors.error || '#d32f2f' : theme.colors.selectableLightBg};
    outline-offset: 2px;
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.error || '#d32f2f' : theme.colors.selectableLightBg};
  border-radius: 4px;
  font-size: 14px;
  background-color: ${({ theme }) => theme.colors.white};

  &:focus {
    outline: 2px solid
      ${({ theme, hasError }) =>
        hasError ? theme.colors.error || '#d32f2f' : theme.colors.selectableLightBg};
    outline-offset: 2px;
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error || '#d32f2f'};
`;

const HelpText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.productTitle};
  font-style: italic;
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.tabBackground};
  border-radius: 4px;
  border-left: 4px solid ${({ theme }) => theme.colors.selectableLightBg};
`;

const InfoIcon = styled.span`
  font-size: 20px;
  line-height: 1;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.notSelectable};
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.selectableLightBg};
    outline-offset: 2px;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.selectableLightBg};

  &:hover {
    background-color: ${({ theme }) => theme.colors.tabBackground};
  }
`;

const SaveButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.selectableLightBg};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    opacity: 0.9;
  }
`;
